import React, {useState, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import "./FormStyles.css";
import {Autocomplete, Button} from "@mui/material";
import {isEmpty, Client} from "weed-js";
import TextField from "@mui/material/TextField";
import {enqueueSnackbar} from "../../redux/notifications/Action";
import {Link} from "react-router-dom";
import Refresh from "@mui/icons-material/Refresh";
import {Banner} from "../../components/elements/Banner.js";
import {ROUTES_ORGANIZATIONS_DETAIL_SUBSCRIPTIONS} from "../../utils/Constants";
import {Item, Product, Subscription} from "../../models/stripe";


export const StripeSubscriptionItem = ({selectedOrganization, subscription, item, product, setSubscriptionItems, setSubscriptions, currentDutchieStores, currentMetrcStores, maxDutchieStores, maxMetrcStores}) => {

    const [quantity, setQuantity] = useState(item.quantity);
    const [error, setError] = useState(null);
    const client = new Client();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!subscription.isActive()) {
            return;
        }
        if (item.isDutchie() && currentDutchieStores > maxDutchieStores && quantity < maxDutchieStores) {
            setQuantity(currentDutchieStores);
            setError("Your plan allows for a maximum of " + maxDutchieStores + " Dutchie store" + (maxDutchieStores > 1 ? "s" : "") + ". Please increase the number of stores to at least " + currentDutchieStores + " to continue.");
        }
        if (item.isMetrc() && currentMetrcStores > maxMetrcStores && quantity < maxMetrcStores) {
            setQuantity(currentMetrcStores);
            setError("Your plan allows for a maximum of " + maxMetrcStores + " Metrc store" + (maxMetrcStores > 1 ? "s" : "") + ". Please increase the number of stores to at least " + currentMetrcStores + " to continue.");
        }
    }, [item]);

    const getSubscriptionStatusIcon = (status) => {
        switch (status) {
        case "active":
            return <span className="subscription-status-icon active">✔️</span>;
        case "past_due":
            return <span className="subscription-status-icon past-due">⚠️</span>;
        case "canceled":
            return <span className="subscription-status-icon canceled">❌</span>;
        case "unpaid":
            return <span className="subscription-status-icon unpaid">💳</span>;
        default:
            return <span className="subscription-status-icon unknown">❓</span>;
        }
    }

    const showTotal = () => {
        const options = [];
        for (let i = 1; i <= 99; i++) {
            options.push({value: i, label: i + " Store" + (i > 1 ? "s" : "")});
        }


        return <>
            {error && <p style={{color: "red"}}>{error}</p>}
            <Autocomplete
                className={"modify-subscription-choose-n-stores"}
                filterSelectedOptions
                disabled={false}
                value={{value: quantity, label: quantity + " Store" + (quantity > 1 ? "s" : "")}}
                fullWidth={false}
                name={"choose-nStores-dropdown"}
                role={"choose-nStores-dropdown"}
                options={options}
                disablePortal={true}
                isOptionEqualToValue={(option, value) => {
                    return option.value === value.value;
                }}
                onChange={(evt, option) => {
                    setQuantity(option.value);
                }}
                renderOption={(props, option) => {
                    return <li {...props} key={"choose-nStores-" + option.value}>
                        {option.label}
                    </li>
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label=""
                        placeholder="---"
                        role={"choose-nStores-dropdown-input"}
                        key={"choose-nStores-dropdown-input"}
                    />
                )}
            />
            for a total of
            ${(parseFloat(quantity) * parseFloat(product.unit_price)).toFixed(2)}/month (+tax)
        </>
    }

    return <li key={item.id} className={"subscription-item"}>
        {getSubscriptionStatusIcon(subscription.status)}
        {product.name}
        {showTotal()}
        <Button
            variant="contained"
            color="primary"
            disabled={quantity === item.quantity}
            onClick={() => {
                client.stripe.api.v1().patch("/subscriptions/change-quantity", {
                    organization_id: selectedOrganization.id,
                    subscription_id: item.subscription_id,
                    subscription_item_id: item.id,
                    store_count: quantity
                }).then((response) => {
                    const updatedItem = response.data;
                    setSubscriptionItems(prevItems => prevItems.map(item => {
                        if (item.external_id === updatedItem.external_id) {
                            return {
                                ...updatedItem
                            };
                        }
                        return item;
                    }));
                    dispatch(enqueueSnackbar({
                        message: "Subscription item updated successfully.",
                        options: {
                            variant: "success"
                        }
                    }));
                }).catch(error => {
                    dispatch(enqueueSnackbar({
                        message: error?.response?.data?.detail || "Failed to create a portal session: " + error.message,
                        options: {
                            variant: "error"
                        }
                    }));
                });
            }}
        >
            Update
        </Button>
        <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
                // Handle cancel subscription logic here
                client.stripe.api.v1().post("/subscriptions/cancel", {
                    organization_id: selectedOrganization.id,
                    subscription_id: item.subscription_id,
                    subscription_item_id: item.id
                }).then((response) => {
                    setSubscriptionItems(prevItems => prevItems.filter(i => i.id !== item.id))
                    dispatch(enqueueSnackbar({
                        message: "Subscription item cancelled successfully.",
                        options: {
                            variant: "success"
                        }
                    }));
                }).catch(error => {
                    dispatch(enqueueSnackbar({
                        message: error?.response?.data?.detail || "Failed to cancel subscription item: " + error.message,
                        options: {
                            variant: "error"
                        }
                    }));
                });
            }}
        >
            Cancel
        </Button>
        <Link
            variant="contained"
            color="primary"
            onClick={() => {
                client.stripe.api.v1().post("/portal-session", {}).then((response) => {
                    const {portal_url} = response.data;
                    window.location.href = portal_url;
                    dispatch(enqueueSnackbar({
                        message: "Redirecting to Stripe portal...",
                        options: {
                            variant: "info"
                        }
                    }));
                }).catch(error => {
                    dispatch(enqueueSnackbar({
                        message: error?.response?.data?.detail || "Failed to create a portal session: " + error.message,
                        options: {
                            variant: "error"
                        }
                    }));
                });
            }}
        >
            Manage with Stripe
        </Link>
    </li>
}


export const StripeSubscription = ({
    subscription,
    subscriptionItems,
    productsMap,
    selectedOrganization,
    setSubscriptions,
    setSubscriptionItems,
    currentDutchieStores,
    currentMetrcStores,
    maxDutchieStores,
    maxMetrcStores
}) => {
    useEffect(() => {
        const itemsBySubscriptionId = subscriptionItems.reduce((acc, item) => {
            if (!acc[item.subscription_id]) {
                acc[item.subscription_id] = [];
            }
            acc[item.subscription_id].push(item);
            return acc;
        }, {});

        if (!itemsBySubscriptionId[subscription.id]) {
            setSubscriptions(prev => prev.filter(sub => sub.id !== subscription.id));
        }
    }, [subscriptionItems]);

    if (!subscription || !subscriptionItems || !productsMap) {
        return null;
    }
    return <div key={subscription.id}>
        <ul>
            {subscriptionItems
                .filter(item => item.subscription_id === subscription.id)
                .map(item => {
                    const product = productsMap[item.product_id];
                    return <StripeSubscriptionItem
                        key={item.id}
                        subscription={subscription}
                        selectedOrganization={selectedOrganization}
                        item={item}
                        product={product}
                        setSubscriptions={setSubscriptions}
                        setSubscriptionItems={setSubscriptionItems}
                        currentDutchieStores={currentDutchieStores}
                        currentMetrcStores={currentMetrcStores}
                        maxDutchieStores={maxDutchieStores}
                        maxMetrcStores={maxMetrcStores}
                    />
                })}
        </ul>
    </div>
}


export const StripeSubscriptions = ({
    subscriptions,
    subscriptionItems,
    subscriptionProducts,
    setSubscriptions,
    setSubscriptionItems,
    selectedOrganization,
    setShowCreateAccountWorkflow,
    setCloseCreateAccountWorkflow,
    currentDutchieStores,
    currentMetrcStores,
    maxDutchieStores,
    maxMetrcStores
}) => {
    const client = new Client();
    const dispatch = useDispatch();

    const refreshButton = (
        <h1>Subscriptions <Refresh style={{cursor: "pointer"}} onClick={() => {
            client.stripe.api.v1().post("/subscriptions/refresh", {
                organization_id: selectedOrganization?.id,
            }).then((response) => {
                Promise.all([
                    Subscription.fetch({client, params: {organization_id: selectedOrganization?.id || null}}),
                    Product.fetch({client, params: {}})
                ]).then(([fetchedSubscriptions, fetchedProducts]) => {
                    setSubscriptions(fetchedSubscriptions);
                    const promises = (fetchedSubscriptions || []).map(subscription => {
                        return Item.fetch({client, params: {subscription_id: subscription.id}})
                    });
                    Promise.all(promises).then(fetchedItemsArrays => {
                        const fetchedItems = fetchedItemsArrays.flat();
                        setSubscriptionItems(fetchedItems || []);
                        dispatch(enqueueSnackbar({
                            message: "Subscriptions refreshed successfully.",
                            options: {
                                variant: "success"
                            }
                        }));
                    }).catch(error => {
                        dispatch(enqueueSnackbar({
                            message: "Error refreshing subscriptions. Please try again.",
                            options: {
                                variant: "error"
                            }
                        }));
                    });
                });
            }).catch(error => {
                dispatch(enqueueSnackbar({
                    message: error?.response?.data?.detail || "Failed to cancel subscription item: " + error.message,
                    options: {
                        variant: "error"
                    }
                }));
            });
        }}/></h1>
    )

    if (isEmpty(subscriptions) || isEmpty(subscriptionItems) || isEmpty(subscriptionProducts)) {
        return <div>{refreshButton}
            <p>You don&apos;t have an active subscription!</p>
            <p>Click
                <a
                    onClick={() => {
                        setShowCreateAccountWorkflow(true)
                        setCloseCreateAccountWorkflow(false);
                    }}
                    className="menubar menu-item">&nbsp;here&nbsp; </a>
                    to set one up.</p>
        </div>
    }

    const productsMap = subscriptionProducts.reduce((acc, product) => {
        acc[product.id] = product;
        return acc;
    }, {});



    return (
        <div>
            {refreshButton}
            {subscriptions.map(subscription => (
                <StripeSubscription
                    key={subscription.id}
                    subscription={subscription}
                    subscriptionItems={subscriptionItems}
                    setSubscriptions={setSubscriptions}
                    setSubscriptionItems={setSubscriptionItems}
                    productsMap={productsMap}
                    selectedOrganization={selectedOrganization}
                    currentDutchieStores={currentDutchieStores}
                    currentMetrcStores={currentMetrcStores}
                    maxDutchieStores={maxDutchieStores}
                    maxMetrcStores={maxMetrcStores}
                />
            ))}
        </div>
    );
}
